import React, { useState } from 'react';
import { Typography, Box, useMediaQuery, Fade, Button, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import LoadingNavBar from '../../components/navbar/LoadingNavBar';
import { Assignment, Badge, Inventory, People } from '@mui/icons-material';
import Footer from '../../components/footer/Footer';
import FeaturesSection from './FeaturedSection';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../resources/strings/StringKeys';

const colors = {
  background: "#ffffff",
  primary: "#102a43",
  secondary: "#39ace7",
  textPrimary: "#212529",
  textSecondary: "#495057",
};

function LandingPage() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const cardsData = [
    { title: t(StringKeys.CAROUSEL_CARD_1_HEADER), description: t(StringKeys.CAROUSEL_CARD_1_DESCRIPTION), icon: <Badge sx={{ fontSize: '80px', color: colors.primary }} />, color: "#ff4f64" },
    { title: t(StringKeys.CAROUSEL_CARD_2_HEADER), description: t(StringKeys.CAROUSEL_CARD_2_DESCRIPTION), icon: <Inventory sx={{ fontSize: '80px', color: colors.primary }} />, color: "#44c4a1" },
    { title: t(StringKeys.CAROUSEL_CARD_3_HEADER), description: t(StringKeys.CAROUSEL_CARD_3_DESCRIPTION), icon: <Assignment sx={{ fontSize: '80px', color: colors.primary }} />, color: "#fcc200" },
    { title: t(StringKeys.CAROUSEL_CARD_4_HEADER), description: t(StringKeys.CAROUSEL_CARD_4_DESCRIPTION), icon: <People sx={{ fontSize: '80px', color: colors.primary }} />, color: "#39ace7" },
  ];

  const cardsToShow = isMobile ? 1 : isTablet ? 2 : 3;
  const visibleCards = Array.from({ length: cardsToShow }, (_, i) => (currentIndex + i) % cardsData.length);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + cardsData.length) % cardsData.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % cardsData.length);
  }; 

  return (
    <>
    <LoadingNavBar showLoginButton={true}/>   
      <Box
        className="hero-section"
        ref={heroRef}
        sx={{
          mt: '55px',
          height: isMobile ? '100%' : '100vh',
          background: 'linear-gradient(135deg, rgba(57, 172, 231, 0.95), rgba(16, 42, 67, 0.95))',
          animation: 'gradientAnimation 15s ease infinite',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '@keyframes gradientAnimation': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}
      >
        <Container maxWidth="lg">
          <Fade in={heroInView} timeout={1500}>
            <Box 
              sx={{ 
                textAlign: 'center',
                py: { xs: 4, sm: 6, md: 8 },
                px: { xs: 2, sm: 4 },
              }}
            >
              <Typography 
                sx={{ 
                  color: "white",
                  fontSize: { 
                    xs: '2rem',
                    sm: '2.5rem',
                    md: '3.5rem'
                  },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: { xs: 2, sm: 3 },
                  WebkitFontSmoothing: 'antialiased',
                }} 
                variant={isMobile ? 'h3' : 'h1'} 
              >
                {t(StringKeys.LANDING_PAGE_TAGLINE_1)}
              </Typography>
              
              <Typography 
                sx={{ 
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: { 
                    xs: '1.125rem',
                    sm: '1.25rem',
                    md: '1.5rem'
                  },
                  lineHeight: 1.6,
                  marginBottom: { xs: 4, sm: 5 },
                  maxWidth: '800px',
                  mx: 'auto',
                  WebkitFontSmoothing: 'antialiased',
                }}
              >
                {t(StringKeys.LANDING_PAGE_TAGLINE_2)}
              </Typography>

              {/* <Button 
                variant="contained"
                sx={{
                  textTransform: 'none',
                  color: 'white',
                  borderRadius: '30px',
                  padding: { xs: '12px 24px', sm: '14px 32px' },
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 600,
                  height: { xs: '48px', sm: '56px' },
                  minWidth: { xs: '200px', sm: '240px' },
                  WebkitTapHighlightColor: 'transparent',
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
                onClick={() => navigate('/signup')}
              >
                {t(StringKeys.SIGN_UP)}
              </Button> */}
            </Box>
          </Fade>
        </Container>
      </Box>

      <Box
        sx={{
          width: '100%',
          background: (theme) => theme.palette.mode === 'dark' 
          ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(9,9,11,0.5) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(242,242,247,0.5) 100%)',
        overflow: 'hidden',

        }}
      >
        <FeaturesSection
          cardsData={cardsData}
          visibleCards={visibleCards}
          handleNext={handleNext}
          handlePrev={handlePrev}
          sx={{
            WebkitOverflowScrolling: 'touch',
            WebkitTransform: 'translateZ(0)',
          }}
        />
      </Box>
    <Footer />
    </>
  );
}

export default LandingPage;
